import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { xml as xmlLang } from '@codemirror/lang-xml'
import {
    getMailFrames, saveCommonMailFrame, resetCommonMailFrame, saveBrandMailFrame, resetBrandMailFrame,
    saveMailFrameValues, uploadMailFrameLogo, resetMailFrameLogo, mailFrameLogoUrl, previewMailFrame,
    type MailFramesState, type MailFrameBrand, type MailFrameValues,
} from '../api'
import { useToast } from '../layout/ToastContext'
import InfoTip from './InfoTip'
import ConfirmDialog from './ConfirmDialog'
import { MAIL_FRAME_PREVIEW_DEBOUNCE_MS, MAIL_LOGO_UPLOAD_MAX_BYTES, MAIL_PREVIEW_BACKGROUND, MAIL_LOGO_WIDTH_MIN, MAIL_LOGO_WIDTH_MAX, DEFAULT_FORM_BRAND } from '../../shared/constants'
import { fill, COMPONENTS_MAIL_FRAME_EDITOR_TEXTS as T } from '../../shared/texts'

// The mail frame (server: email/frames.ts). Two places, one component:
//   mode 'common' — the frame every brand uses unless it has its own.
//   mode 'brand'  — logo, logo width, footer colours, optionally an own frame.
// Both sit on System → Vorlagen → E-Mail (pages/MailTemplates.tsx), since 2026-09-27; before, in Einstellungen.
//
// ⛔ An edit here is part of the PAGE's changed state: the ● on the pill, „Übernehmen", „Verwerfen"
// and the guard when the tab is closed. Until 2026-09-27 the frame had save buttons of its own and
// never told the page — the operator typed, saw nothing change up top, and reported „modified wird
// nicht ausgelöst". The component therefore reports through `bind` (dirty + save + discard) and
// has no save button of its own. What stays immediate — with a confirmation where it deletes — is
// what is not an edit: uploading a logo, resetting to the default, removing the brand's own frame.

export interface FrameCtl {
    dirty: boolean
    save: () => Promise<void>
    discard: () => void
}

const SOURCE_LABEL: Record<MailFrameBrand['activeSource'], string> = T.sourceLabels

function PlaceholderHelp() {
    return (
        <p className="hint" style={{ margin: '0 0 8px' }}>
            {T.help1}<code>{'{content}'}</code>{T.help2}<b>{T.helpRequired}</b>), <code>{'{logo}'}</code>{T.help3}<code>{'<img src="{logo}">'}</code>), <code>{'{logo_width}'}</code>, <code>{'{footer_color}'}</code>,{' '}
            <code>{'{footer_text_color}'}</code>, <code>{'{impressum_url}'}</code>, <code>{'{brand_name}'}</code>{T.help4}<code>{'{#impressum_url}'}…{'{/impressum_url}'}</code>{T.help5}
        </p>
    )
}

function Preview({ brand, frame, values }: { brand: string, frame?: string, values?: MailFrameValues }) {
    const [html, setHtml] = useState('')
    const [error, setError] = useState('')
    const key = JSON.stringify([brand, frame, values])
    useEffect(() => {
        const t = setTimeout(() => {
            previewMailFrame(brand, frame, values)
                .then((h) => { setHtml(h); setError('') })
                .catch((e) => setError(e.message))
        }, MAIL_FRAME_PREVIEW_DEBOUNCE_MS)
        return () => clearTimeout(t)
    }, [key]) // eslint-disable-line react-hooks/exhaustive-deps
    return (
        <div>
            {error && <div className="notice err" style={{ marginBottom: 6 }}>{error}</div>}
            {/* sandbox without flags: no script, no form, no navigation out of the preview */}
            <iframe title={T.previewTitle} sandbox="" srcDoc={html}
                style={{ width: '100%', height: 480, border: '1px solid var(--line)', borderRadius: 6, background: MAIL_PREVIEW_BACKGROUND }} />
        </div>
    )
}

function FrameCode({ value, onChange, readOnly }: { value: string, onChange: (v: string) => void, readOnly: boolean }) {
    return (
        <div style={{ border: '1px solid var(--line)', borderRadius: 6, overflow: 'hidden' }}>
            <CodeMirror value={value} height="480px" extensions={[xmlLang()]} editable={!readOnly} onChange={onChange}
                basicSetup={{ lineNumbers: true, foldGutter: true, highlightActiveLine: true }} />
        </div>
    )
}

// The settings page is one <form>: Enter in a field here would save the whole page instead.
const noEnter = (e: KeyboardEvent) => { if (e.key === 'Enter') e.preventDefault() }

const split = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 12, alignItems: 'start' } as const

// Reports to the page: `dirty` on every change, save/discard always calling the latest closures.
// On unmount the page is told there is nothing left unsaved here.
function useBind(bind: ((ctl: FrameCtl | null) => void) | undefined, dirty: boolean, save: () => Promise<void>, discard: () => void) {
    const latest = useRef({ save, discard })
    latest.current = { save, discard }
    useEffect(() => {
        bind?.({ dirty, save: () => latest.current.save(), discard: () => latest.current.discard() })
    }, [dirty]) // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => () => bind?.(null), []) // eslint-disable-line react-hooks/exhaustive-deps
}

type Props = { readOnly: boolean, bind?: (ctl: FrameCtl | null) => void }
    & ({ mode: 'common' } | { mode: 'brand', brand: string })

export default function MailFrameEditor(props: Props) {
    const { toast } = useToast()
    const [state, setState] = useState<MailFramesState | null>(null)
    const [loadError, setLoadError] = useState('')
    const reload = () => getMailFrames().then((s) => { setState(s); setLoadError('') }).catch((e) => setLoadError(e.message))
    useEffect(() => { void reload() }, [])

    if (loadError) return <div className="notice err">{fill(T.loadFailed, { error: loadError })}</div>
    if (!state) return <p className="muted">{T.loading}</p>
    return props.mode === 'common'
        ? <CommonFrame state={state} readOnly={props.readOnly} reload={reload} toast={toast} bind={props.bind} />
        : <BrandFrame key={props.brand} state={state} brand={props.brand} readOnly={props.readOnly} reload={reload} toast={toast} bind={props.bind} />
}

type Toast = ReturnType<typeof useToast>['toast']
type Inner = { state: MailFramesState, readOnly: boolean, reload: () => Promise<void>, toast: Toast, bind?: (ctl: FrameCtl | null) => void }

function CommonFrame({ state, readOnly, reload, toast, bind }: Inner) {
    const [html, setHtml] = useState(state.common.html)
    const [previewBrand, setPreviewBrand] = useState(state.brands[0]?.brand ?? DEFAULT_FORM_BRAND)
    const [busy, setBusy] = useState(false)
    const [confirmReset, setConfirmReset] = useState(false)
    useEffect(() => { setHtml(state.common.html) }, [state.common.html])
    const dirty = !readOnly && html !== state.common.html
    const own = state.brands.filter((b) => b.activeSource === 'brand')

    // Called by the page's „Übernehmen"; an error is thrown on, so the page shows it.
    useBind(bind, dirty, async () => { await saveCommonMailFrame(html); await reload() }, () => setHtml(state.common.html))

    const reset = async () => {
        setBusy(true)
        try { await resetCommonMailFrame(); await reload(); toast(T.commonReset) }
        finally { setBusy(false); setConfirmReset(false) }
    }

    return (
        <>
            <p className="muted" style={{ marginTop: 0 }}>
                {T.common1}<b>{T.commonAllBrands}</b>{T.common2}{' '}
                {state.common.stored ? <b>{T.commonStored}</b> : <>{T.commonDefault1}<b>{T.commonDefaultBold}</b>.</>}
                {own.length > 0 && <>{fill(T.commonOwn, { names: own.map((b) => b.name).join(', ') })}</>}
                {' '}{T.savedWith}<b>{T.applyQuoted}</b>{T.above}
            </p>
            <PlaceholderHelp />
            <div style={split}>
                <FrameCode value={html} onChange={setHtml} readOnly={readOnly} />
                <div>
                    <div className="row" style={{ marginBottom: 6 }}>
                        <span className="hint">{T.previewFor}</span>
                        <select value={previewBrand} onChange={(e) => setPreviewBrand(e.target.value)} style={{ width: 'auto' }}>
                            {state.brands.map((b) => <option key={b.brand} value={b.brand}>{b.name}</option>)}
                        </select>
                    </div>
                    <Preview brand={previewBrand} frame={html} />
                </div>
            </div>
            {!readOnly && (
                <div className="row" style={{ marginTop: 10 }}>
                    <button type="button" className="secondary" onClick={() => setHtml(state.defaultFrame)} disabled={busy || html === state.defaultFrame}>
                        {T.loadDefault}
                    </button>
                    {state.common.stored && (
                        <button type="button" className="secondary" onClick={() => setConfirmReset(true)} disabled={busy || dirty}
                            title={dirty ? T.applyOrDiscardFirst : undefined}>
                            {T.resetToDefault}
                        </button>
                    )}
                    {dirty && <span className="hint">{T.changedHint}</span>}
                </div>
            )}
            <ConfirmDialog open={confirmReset} title={T.resetCommonTitle}
                message={T.resetCommonMessage}
                confirmLabel={T.reset} danger busy={busy} onConfirm={() => void reset()} onCancel={() => setConfirmReset(false)} />
        </>
    )
}

function ColorField({ label, value, fallback, onChange }: { label: string, value: string, fallback: string, onChange: (v: string) => void }) {
    return (
        <div className="field">
            <label>{label}</label>
            <div className="row" style={{ flexWrap: 'nowrap' }}>
                <input type="color" value={value || fallback} style={{ flex: '0 0 44px', width: 44, minWidth: 44, height: 34, padding: 2 }}
                    onChange={(e) => onChange(e.target.value)} />
                <input type="text" placeholder={fallback} value={value} onKeyDown={noEnter} onChange={(e) => onChange(e.target.value)}
                    style={{ flex: '0 0 110px', width: 110 }} />
            </div>
        </div>
    )
}

function BrandFrame({ state, brand, readOnly, reload, toast, bind }: Inner & { brand: string }) {
    const b = state.brands.find((x) => x.brand === brand)
    const [values, setValues] = useState<MailFrameValues>({})
    const [useOwn, setUseOwn] = useState(false)
    const [html, setHtml] = useState('')
    const [busy, setBusy] = useState(false)
    const [confirmRemove, setConfirmRemove] = useState(false)
    const [logoBust, setLogoBust] = useState(Date.now())
    const fileRef = useRef<HTMLInputElement>(null)

    const fromStored = () => {
        if (!b) return
        setValues({ logoWidth: b.values.logoWidth ?? '', footerColor: b.values.footerColor ?? '', footerTextColor: b.values.footerTextColor ?? '' })
        setUseOwn(b.frame != null)
        setHtml(b.frame ?? state.common.html)
    }
    useEffect(fromStored, [b?.frame, b?.values.logoWidth, b?.values.footerColor, b?.values.footerTextColor, state.common.html]) // eslint-disable-line react-hooks/exhaustive-deps

    const same = (a: unknown, c: unknown) => String(a ?? '') === String(c ?? '')
    const valuesDirty = !!b && (!same(values.logoWidth, b.values.logoWidth) || !same(values.footerColor, b.values.footerColor)
        || !same(values.footerTextColor, b.values.footerTextColor))
    // Switching the own frame ON with nothing stored is a change as well — the page must offer to save it.
    const frameDirty = !!b && useOwn && (b.frame == null || html !== b.frame)
    const dirty = !readOnly && (valuesDirty || frameDirty)

    useBind(bind, dirty, async () => {
        if (valuesDirty) await saveMailFrameValues(brand, values)
        if (frameDirty) await saveBrandMailFrame(brand, html)
        await reload()
    }, fromStored)

    if (!b) return null

    const run = async (fn: () => Promise<unknown>, ok: string) => {
        setBusy(true)
        try { await fn(); await reload(); toast(ok) }
        catch (e: any) { toast(e.message, 'err') }
        finally { setBusy(false) }
    }
    const onLogo = async (file: File | undefined) => {
        if (!file) return
        await run(() => uploadMailFrameLogo(brand, file), fill(T.logoUploaded, { brand: b.name }))
        setLogoBust(Date.now())
        if (fileRef.current) fileRef.current.value = ''
    }

    return (
        <>
            <p className="muted" style={{ marginTop: 0 }}>
                {T.brand1}<b>{SOURCE_LABEL[b.activeSource]}</b>{T.brand2}<b>{T.applyQuoted}</b>{T.brand3}
            </p>

            <div className="row" style={{ alignItems: 'flex-end' }}>
                <div className="field">
                    <label>{T.logo} <InfoTip>
                        {fill(T.logoTip, { mb: MAIL_LOGO_UPLOAD_MAX_BYTES / (1024 * 1024) })}
                    </InfoTip></label>
                    <div className="row">
                        <img src={mailFrameLogoUrl(brand, logoBust)} alt={fill(T.logoAlt, { brand: b.name })}
                            style={{ maxHeight: 64, maxWidth: 220, background: MAIL_PREVIEW_BACKGROUND, border: '1px solid var(--line)', borderRadius: 4, padding: 4 }} />
                        <span className="hint">{b.logoUploaded ? T.uploaded : T.default}</span>
                    </div>
                </div>
                {!readOnly && (
                    <div className="field">
                        <input ref={fileRef} type="file" accept="image/png,image/jpeg" style={{ display: 'none' }}
                            onChange={(e) => void onLogo(e.target.files?.[0])} />
                        <div className="row">
                            <button type="button" className="secondary" disabled={busy} onClick={() => fileRef.current?.click()}>{T.uploadLogo}</button>
                            {b.logoUploaded && (
                                <button type="button" className="secondary" disabled={busy}
                                    onClick={() => void run(() => resetMailFrameLogo(brand), T.logoReset).then(() => setLogoBust(Date.now()))}>
                                    {T.defaultLogo}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>

            <div className="row" style={{ alignItems: 'flex-end' }}>
                <div className="field" style={{ maxWidth: 160 }}>
                    <label>{T.logoWidth} <span className="hint">{T.logoWidthUnit}</span></label>
                    <input type="number" min={MAIL_LOGO_WIDTH_MIN} max={MAIL_LOGO_WIDTH_MAX} step={1} placeholder={String(b.defaults.logoWidth)}
                        value={values.logoWidth ?? ''} onKeyDown={noEnter}
                        onChange={(e) => setValues((v) => ({ ...v, logoWidth: e.target.value === '' ? '' : Number(e.target.value) }))} />
                </div>
                <ColorField label={T.footerColor} value={values.footerColor ?? ''} fallback={b.defaults.footerColor}
                    onChange={(c) => setValues((v) => ({ ...v, footerColor: c }))} />
                <ColorField label={T.footerTextColor} value={values.footerTextColor ?? ''} fallback={b.defaults.footerTextColor}
                    onChange={(c) => setValues((v) => ({ ...v, footerTextColor: c }))} />
                <span className="hint" style={{ alignSelf: 'center' }}>{T.emptyIsDefault}</span>
            </div>

            <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px' }}>
                <input type="checkbox" checked={useOwn} disabled={readOnly || busy}
                    onChange={(e) => {
                        if (e.target.checked) { setUseOwn(true); if (b.frame == null) setHtml(state.common.html) }
                        else if (b.frame != null) setConfirmRemove(true)
                        else setUseOwn(false)
                    }} />
                {fill(T.ownFrameFor, { brand: b.name })} <span className="hint">{T.ownFrameOtherwise}</span>
            </label>

            {useOwn && <PlaceholderHelp />}
            <div style={split}>
                {useOwn && <FrameCode value={html} onChange={setHtml} readOnly={readOnly} />}
                <div>
                    <div className="hint" style={{ marginBottom: 6 }}>{T.previewWithSample}{dirty ? T.previewWithChanges : ''}</div>
                    <Preview brand={brand} frame={useOwn ? html : undefined} values={values} />
                </div>
            </div>
            <ConfirmDialog open={confirmRemove} title={fill(T.removeOwnTitle, { brand: b.name })}
                message={T.removeOwnMessage}
                confirmLabel={T.remove} danger busy={busy}
                onConfirm={() => void run(() => resetBrandMailFrame(brand), fill(T.backToCommon, { brand: b.name })).then(() => setConfirmRemove(false))}
                onCancel={() => setConfirmRemove(false)} />
        </>
    )
}
